import React, { useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { showCustomDialog } from '../../helpers/DialogHelper';
import ChooseKeywords from '../../components/common/choose-keywords/ChooseKeywords';
import ChooseActiveBrands from '../../components/common/choose-keywords/ChooseActiveBrands';
import { t } from 'i18next';
import { KeywordResponse } from '../../models/monitor/KeywordResponse';
import { TagsResponse } from '../../models/tags/TagsResponse';
import { useUpdateProjectOptionsKeywords } from '../../api/hooks/project/options/useUpdateProjectOptionsKeywords';
import { invalidateBrandStats } from '../../api/query/invalidateBrandStats';

/**
 * `keywordsPage` — konto ma addon `KEYWORDS_PAGE`: okienko przełącza status marek (to samo co strona
 * Brands) zamiast zapisywać osobny wybór w `project_options.keywords`. Bez addonu działa jak dotąd.
 */
const useChooseKeywords = ({
  refreshProjects,
  keywordsPage = false,
}: {
  refreshProjects: () => void;
  keywordsPage?: boolean;
}) => {
  const queryClient = useQueryClient();
  const { mutateAsync: updateProjectOptionsKeywords } = useUpdateProjectOptionsKeywords();
  const selectedKeywordsRef = useRef<string[]>([]);
  const brandsChangedRef = useRef(false);

  const updateKeywords = async (id: number, selectedKeywords: string[]) => {
    try {
      await updateProjectOptionsKeywords({
        projectId: id,
        keywords: selectedKeywords,
      });

      // Use the page's onRefresh (silent in-place refetch). Do NOT call the context's
      // refetchData() here — it nulls accountResponse, which flips ProjectsPreviewPageSwitch
      // off V2 and remounts the page, re-firing useProjectNavigate → redirect to /projects.
      await refreshProjects();
    } catch (error) {
      console.error('Error updating keywords:', error);
    }
  };

  // Przełączniki zapisują od razu — dashboard odświeżamy raz, przy zamknięciu okna, i tylko po zmianie.
  const refreshAfterBrandChanges = async () => {
    if (!brandsChangedRef.current) {
      return;
    }
    brandsChangedRef.current = false;
    await invalidateBrandStats(queryClient);
    await refreshProjects();
  };

  const confirmNewProjectKeywords = (
    projectId: number,
    getProjectName: () => string,
    keywordsResponse: KeywordResponse[],
    tags: TagsResponse[],
    liveKeys?: string[],
    defaultKeyword?: string
  ) => {
    if (keywordsPage) {
      brandsChangedRef.current = false;
      showCustomDialog({
        header: t('selectBrandForAnalysis'),
        message: (
          <ChooseActiveBrands
            projectId={projectId}
            firmName={getProjectName().trim()}
            defaultKeyword={defaultKeyword ?? ''}
            onChanged={() => (brandsChangedRef.current = true)}
          />
        ),
        // Zmiany zapisują się od razu, więc bez stopki — zamyka krzyżyk, jak okno marki na stronie Brands.
        className: 'dialog-box dialog-box--no-footer',
        hideFooter: true,
        accept: () => undefined,
        onHide: () => void refreshAfterBrandChanges(),
      });
      return;
    }

    showCustomDialog({
      header: t('selectBrandForAnalysis'),
      message: (
        <ChooseKeywords
          projectId={projectId}
          keywords={keywordsResponse}
          tags={tags}
          projectName={getProjectName().trim()}
          onChangeProject={(keywords: string[]) => (selectedKeywordsRef.current = keywords)}
          liveKeys={liveKeys}
          defaultKeyword={defaultKeyword}
        />
      ),
      acceptLabel: t('save'),
      rejectLabel: t('resetToDefault'),
      accept: () => updateKeywords(projectId, selectedKeywordsRef.current),
      reject: () => updateKeywords(projectId, []),
    });
  };

  return { confirmNewProjectKeywords };
};

export default useChooseKeywords;
